import { useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from '../../i18n/i18n';
import { FILTER_MULTI_VALUE_DELIMITER, FILTER_RANGE_VALUE_DELIMITER, FilterContainer } from '../../state/filters';
import { CategoryTreeContainer } from '../../state/categories';
import { UiContainer } from '../../state/ui';
import { ApiFacetLoop54, Attribute } from '../../types';
import { Button } from '../Form/Button';
import { filter_panel_close, Icon } from '../Icon/Icon';
import { Overlay } from '../Overlay/Overlay';
import { toQuery } from '../Routes';
import './FilterPanel.css';
import { FilterPanelContent } from './FilterPanelContent/FilterPanelContent';

export const FilterPanel = ({
	perma,
	filterPage,
	filterEventData,
	facets,
	limitedCustomFilters = [],
	sortOverride,
}: {
	perma: string;
	filterPage: 'category' | 'brands';
	filterEventData: Record<string, any>;
	facets?: ApiFacetLoop54[];
	limitedCustomFilters?: string[]; // decides which custom filters to use. if empty all custom filters will be used
	sortOverride?: Attribute[];
}) => {
	const ui = UiContainer.useContainer();
	const categoryTree = CategoryTreeContainer.useContainer();
	const { resetFilters, updateFilters, filters, setInitialMinMax, getRangeSelectionForFacet } =
		FilterContainer.useContainer();
	const history = useHistory();
	const location = useLocation();
	const permaRef = useRef<string | null>(null);
	const navigationPermaRef = useRef<string | null>(null);
	useEffect(() => {
		// update initial min and max on new category permas
		if (permaRef.current !== perma && facets) {
			permaRef.current = perma;
			const priceFacet = facets.find((facet) => facet.type === 'range' && facet.name === 'price');
			if (priceFacet && priceFacet.type === 'range') {
				setInitialMinMax([priceFacet.min, priceFacet.max]);
			}
		}
	}, [facets, perma]);

	useEffect(() => {
		// reset filters when navigating to a different category/brand (skip first render)
		if (navigationPermaRef.current && navigationPermaRef.current !== perma) {
			resetFilters();
		}
		navigationPermaRef.current = perma;
	}, [perma, resetFilters]);

	// update query params when filters change
	useEffect(() => {
		const searchParams = new URLSearchParams(location.search);
		const existingParams = Array.from(searchParams.entries()).reduce(
			(acc, [key, value]) => {
				acc[key] = value;
				return acc;
			},
			{} as Record<string, string>
		);

		const appliedFilters = filters.reduce(
			(acc, filter) => {
				if (filter.type === 'distinct' && filter.items) {
					const appliedItems = filter.items.filter((item) => item.selected).map((item) => item.item);

					if (appliedItems.length > 0) {
						acc[filter.name] = appliedItems.join(FILTER_MULTI_VALUE_DELIMITER);
					} else {
						delete acc[filter.name]; // remove filter if no applied items
					}
				} else if (filter.type === 'range') {
					const rangeSelection = getRangeSelectionForFacet(filter);

					if (!rangeSelection) {
						delete acc[filter.name];
						return acc;
					}

					acc[filter.name] = `${rangeSelection.min}${FILTER_RANGE_VALUE_DELIMITER}${rangeSelection.max}`;
				}
				return acc;
			},
			{ ...existingParams } // merge with existing params
		);

		Object.keys(existingParams).forEach((key) => {
			if (!(key in appliedFilters)) {
				delete appliedFilters[key];
			}
		});

		const nextQuery = toQuery(appliedFilters);
		const nextSearch = nextQuery ? `?${nextQuery}` : '';

		if (nextSearch !== location.search && (Object.keys(appliedFilters).length !== 0 || location.search !== '')) {
			history.push({
				pathname: location.pathname,
				search: nextSearch,
				hash: location.hash,
			});
		}
	}, [filters]);

	useEffect(() => {
		// reset filters when unmounting
		return () => {
			resetFilters();
		};
	}, []);

	// Track when FilterPanel opens with supercategory context
	const panelOpenRef = useRef(false);
	useEffect(() => {
		if (ui.isFilterPanelOpen && !panelOpenRef.current) {
			// Panel just opened
			const supercategoryId = categoryTree.getSupercategoryId(perma);
			const supercategoryName = categoryTree.getCategoryById(supercategoryId)?.name;

			// Only track if we have a valid supercategory (skip brand pages or missing data)
			if (typeof window.plausible !== 'undefined' && supercategoryName) {
				window.plausible('filterPanelOpened', {
					props: { supercategory: supercategoryName },
				});
			}
		}
		panelOpenRef.current = ui.isFilterPanelOpen;
	}, [ui.isFilterPanelOpen, perma, categoryTree]);

	const handleResetFilters = () => {
		resetFilters();
	};

	const handleFilterChange = (filterName: string, itemValue: string | [number, number], isApplied: boolean) => {
		if (typeof window.plausible !== 'undefined') {
			if (filterPage === 'category') {
				window.plausible('categoryFiltered', { props: filterEventData });
			} else if (filterPage === 'brands') {
				window.plausible('brandFiltered', { props: filterEventData });
			}
		}
		updateFilters(filterName, itemValue, isApplied);
	};

	const apply = () => {
		ui.toggleFilterPanel();
	};

	return (
		<>
			{ui.isFilterPanelOpen && (
				<Overlay
					toggle={ui.toggleFilterPanel}
					className="filter-panel-overlay"
					onClick={ui.toggleFilterPanel}
				/>
			)}
			<div className={`filter-panel-wrapper ${ui.isFilterPanelOpen ? 'open' : ''}`}>
				<div id="filter-panel" className={ui.isFilterPanelOpen ? 'open' : ''}>
					<div className="wrapper">
						<TopBar />
						<FilterPanelContent
							filters={facets ?? filters}
							onFilterChange={handleFilterChange}
							limitedCustomFilters={limitedCustomFilters}
							sortOverride={sortOverride}
						/>
						<BottomBar onApply={apply} onReset={handleResetFilters} />
					</div>
				</div>
			</div>
		</>
	);
};

const TopBar = () => {
	const t = useTranslation().filtering;
	const ui = UiContainer.useContainer();
	return (
		<div className="top-bar">
			<div className="column left-column">
				<p>{t.filterSingular}</p>
			</div>
			<div className="column right-column" onClick={() => ui.toggleFilterPanel()}>
				<Icon shape={filter_panel_close} />
			</div>
		</div>
	);
};

const BottomBar = ({ onApply, onReset }: { onApply: () => void; onReset: () => void }) => {
	const t = useTranslation().filtering;
	const { getNumOfAppliedFilters } = FilterContainer.useContainer();
	const numOfAppliedFilters = getNumOfAppliedFilters();
	const ui = UiContainer.useContainer();

	return (
		<div className={`bottom-bar ${ui.isFilterPanelOpen ? 'open' : ''}`}>
			<div className="buttons">
				<div className="filter-button-wrapper">
					<Button className="filter-button erase" onClick={onReset}>
						{t.clear} {numOfAppliedFilters > 0 && <>({numOfAppliedFilters})</>}
					</Button>
				</div>
				<div className="filter-button-wrapper">
					<Button className="filter-button apply" onClick={onApply}>
						{t.apply}
					</Button>
				</div>
			</div>
		</div>
	);
};
